import { AvailableIcon } from "src/ccl/document";
import { tokens } from "src/ccl/stitches/theme";
import {
  User,
  Assignee,
  TalentLocation,
  Pronouns,
  TalentVertical,
  VerticalConfiguration,
  Job,
  TalentProfile,
  JobTalent,
  JobTalentState,
  JobKind,
  CompanyHeadcountEstimate,
} from "src/graphql/types";
import { uppercaseFirstLetter } from "src/utils/lang";

export const assigneeAvatarData = (user: User | undefined) => {
  let assignees: Assignee[] = [];

  if (user === undefined) {
    return assignees;
  }

  if (user?.agencyUsers) {
    assignees = user.agencyUsers;
  }

  if (user) {
    assignees = [
      {
        id: user.id,
        name: user.name,
        featuredAsset: user.featuredAsset,
        assignedLiveJobCount: user.assignedLiveJobCount,
      },
      ...assignees,
    ];
  }

  return assignees;
};

export const getFullTravelLocation: (
  t: string,
  additionalLocations: TalentLocation[],
) => string | undefined = (travelLocation, additionalLocations) => {
  const locations = (additionalLocations || []).filter((l) =>
    l.name.toLowerCase().startsWith(travelLocation.toLowerCase()),
  );

  if (locations.length === 0) {
    return;
  }

  return locations
    .sort((a, b) => a.name.length - b.name.length)[0]
    .name.split(",")[0];
};

export const pronounsMap = {
  [Pronouns.HeHim]: "He/Him",
  [Pronouns.SheHer]: "She/Her",
  [Pronouns.TheyThem]: "They/Them",
  [Pronouns.ZeHir]: "Ze/Hir",
  [Pronouns.HeThey]: "He/They",
  [Pronouns.SheThey]: "She/They",
};

export const legacyPronounsMap: Record<string, string> = {
  "He/Him": "he_him",
  "She/Her": "she_her",
  "They/Them": "they_them",
  "Ze/Hir": "ze_hir",
  "He/They": "he_they",
  "She/They": "she_they",
};

// Each map names every vertical this build knows, and the Record types make
// codegen's next vertical a compile error until it is named here. Read them
// through the helpers below, never directly: the API can send a vertical newer
// than this build, which the helpers name rather than crash on.
export const verticalMap: Record<TalentVertical, string> = {
  [TalentVertical.FashionModel]: "model",
  [TalentVertical.Photographer]: "photographer",
  [TalentVertical.Hmua]: "hair & makeup artist",
  [TalentVertical.Influencer]: "influencer",
};

export const verticalJobTypeMap: Record<TalentVertical, string> = {
  [TalentVertical.FashionModel]: "modelling",
  [TalentVertical.Photographer]: "photography",
  [TalentVertical.Hmua]: "hair & makeup",
  [TalentVertical.Influencer]: "influencer",
};

export const verticalIconMap: Record<TalentVertical, AvailableIcon> = {
  [TalentVertical.FashionModel]: "polaroids",
  [TalentVertical.Photographer]: "cameraPhoto",
  [TalentVertical.Hmua]: "hairStylist",
  [TalentVertical.Influencer]: "influencer",
};

export const verticalIconMapAlt: Record<TalentVertical, AvailableIcon> = {
  [TalentVertical.FashionModel]: "polaroidsAlt",
  [TalentVertical.Photographer]: "cameraPhotoAlt",
  [TalentVertical.Hmua]: "hairStylistAlt",
  [TalentVertical.Influencer]: "influencer",
};

export const verticalIconMapAltOutline: Record<TalentVertical, AvailableIcon> =
  {
    [TalentVertical.FashionModel]: "polaroidAltOutline",
    [TalentVertical.Photographer]: "cameraPhotoAltOutline",
    [TalentVertical.Hmua]: "hairStylistAltOutline",
    [TalentVertical.Influencer]: "influencer",
  };

export const verticalIconBackgroundMap: Record<
  TalentVertical,
  keyof typeof tokens.colors
> = {
  [TalentVertical.FashionModel]: "lightOrange",
  [TalentVertical.Photographer]: "lightGreen",
  [TalentVertical.Hmua]: "lightPurple",
  [TalentVertical.Influencer]: "lightPurple",
};

const knownVerticals: string[] = Object.values(TalentVertical);

export const isKnownVertical = (
  vertical: string | null | undefined,
): vertical is TalentVertical => knownVerticals.includes(vertical as string);

// The API's own spelling, as the Open Calls role label already reads it:
// SOUND_ENGINEER is "sound engineer". A missing vertical reads as nothing.
const humanizeVertical = (vertical: string | null | undefined) =>
  (vertical || "").toLowerCase().replaceAll("_", " ");

const FALLBACK_VERTICAL_ICON: AvailableIcon = "user";

type MaybeVertical = string | null | undefined;

export const verticalLabel = (vertical: MaybeVertical) =>
  isKnownVertical(vertical)
    ? verticalMap[vertical]
    : humanizeVertical(vertical);

export const verticalPluralLabel = (vertical: MaybeVertical) =>
  `${uppercaseFirstLetter(verticalLabel(vertical))}s`;

export const verticalJobTypeLabel = (vertical: MaybeVertical) =>
  isKnownVertical(vertical)
    ? verticalJobTypeMap[vertical]
    : humanizeVertical(vertical);

export const verticalIcon = (vertical: MaybeVertical) =>
  isKnownVertical(vertical)
    ? verticalIconMap[vertical]
    : FALLBACK_VERTICAL_ICON;

export const verticalIconAlt = (vertical: MaybeVertical) =>
  isKnownVertical(vertical)
    ? verticalIconMapAlt[vertical]
    : FALLBACK_VERTICAL_ICON;

export const verticalIconAltOutline = (vertical: MaybeVertical) =>
  isKnownVertical(vertical)
    ? verticalIconMapAltOutline[vertical]
    : FALLBACK_VERTICAL_ICON;

export const verticalIconBackground = (
  vertical: MaybeVertical,
): keyof typeof tokens.colors =>
  isKnownVertical(vertical) ? verticalIconBackgroundMap[vertical] : "grey1";

export const jobTypeMap: Record<string, string> = {
  [JobKind.InfluencerBrief]: "Influencer",
  [JobKind.StudentAndGradProject]: "Student project",
  [JobKind.Ecom]: "Ecommerce",
};

export const jobTypeIconMap: Record<string, AvailableIcon> = {
  [JobKind.InfluencerBrief]: "influencer",
  [JobKind.Fitting]: "fitting",
  [JobKind.Other]: "other",
  [JobKind.Gifting]: "gifting",
  [JobKind.StudentAndGradProject]: "studentProject",
  [JobKind.TestShoot]: "testShoot",
  [JobKind.MusicVideo]: "musicVideo",
  [JobKind.Lookbook]: "lookbook",
  [JobKind.Casting]: "casting",
  [JobKind.Campaign]: "campaign",
  [JobKind.Commercial]: "commerical",
  [JobKind.Ecom]: "ecom",
  [JobKind.Editorial]: "editorial",
  [JobKind.FashionWeek]: "fashionWeek",
  [JobKind.Events]: "events",
};

export const getConfigForVertical = (
  configs: VerticalConfiguration[],
  vertical: TalentVertical,
) => {
  return configs.find(
    (config: VerticalConfiguration) => config.name === vertical?.toLowerCase(),
  );
};

// The verticals gloss may offer this viewer, in the API's order: the ones this
// build knows that the API's verticalConfigurations also offers. Empty until
// that list arrives, so gloss never offers a vertical the API has not (R15).
export const offeredVerticals = (
  configs: VerticalConfiguration[] | null | undefined,
): TalentVertical[] =>
  (configs || []).map(({ id }) => id).filter(isKnownVertical);

// Whether gloss may use a vertical it read back from a link: this build must
// know it and, once the API's list has loaded, the API must offer it. A known
// vertical passes until the list arrives, so a link keeps its filter on a
// cold load.
export const isUsableVertical = (
  vertical: string | null | undefined,
  configs: VerticalConfiguration[] | null | undefined,
): vertical is TalentVertical =>
  isKnownVertical(vertical) &&
  (!configs || offeredVerticals(configs).includes(vertical));

// Reads a ?v= label ("models", "hair & makeup artists") as a vertical, or
// undefined for one gloss may not use.
export const verticalEnum = (
  label: string,
  configs: VerticalConfiguration[] | null | undefined,
): TalentVertical | undefined => {
  const vertical = (() => {
    switch (label.replace(/%20/g, " ").toLowerCase()) {
      case "models":
        return TalentVertical.FashionModel;
      case "model":
        return TalentVertical.FashionModel;
      case "hair & makeup artists":
        return TalentVertical.Hmua;
      default:
        return label.replace(/s$/, "").toLocaleUpperCase();
    }
  })();

  return isUsableVertical(vertical, configs) ? vertical : undefined;
};

// The config of the first talent whose vertical has one: a mixed job can lead
// with someone in a vertical this viewer has no config for.
export const getVerticalConfigForJob = (
  configs: VerticalConfiguration[],
  job: Job,
) => {
  for (const { talent } of job.talent || []) {
    const vertical = (talent.profile as TalentProfile | undefined)?.vertical;
    const config = vertical && getConfigForVertical(configs, vertical);
    if (config) {
      return config;
    }
  }

  return undefined;
};

export const getDeliverableValuesFromConfig = (
  config: VerticalConfiguration | undefined,
) => {
  if (!config) {
    return [];
  }

  const field = config.jobCreationSchema.detailsFormFields.find(
    (field) => field.name === "Deliverables",
  );

  if (!field) {
    return [];
  }

  return field.props?.checkboxOptions || [];
};

export const getTalentVerticals = (talentUsers: User[]) =>
  Array.from(
    new Set(talentUsers.map((u) => (u.profile as TalentProfile).vertical)),
  );

export const confirmedVerticalMember = (
  vertical: TalentVertical,
  jobTalent: JobTalent,
): boolean => {
  const user = jobTalent.talent;
  return (
    isVerticalMember(vertical, user) &&
    [JobTalentState.Confirmed, JobTalentState.Paid].includes(jobTalent.state)
  );
};

export const isVerticalMember = (
  vertical: TalentVertical,
  user: User,
): boolean => (user.profile as TalentProfile).vertical === vertical;

export const companyHeadcountEstimateMap = {
  [CompanyHeadcountEstimate.OneToFive]: "1-5",
  [CompanyHeadcountEstimate.SixToTwenty]: "6-20",
  [CompanyHeadcountEstimate.TwentyOneToFifty]: "21-50",
  [CompanyHeadcountEstimate.FiftyOneToOneHundred]: "51-100",
  [CompanyHeadcountEstimate.OneHundredOneToFiveHundred]: "101-500",
  [CompanyHeadcountEstimate.AboveFiveHundred]: "500+",
};

export const surfacedFromOptions = [
  "Social media",
  "Through a friend",
  "Search engine (Google, Yahoo, Bing)",
  "Blog or publication",
  "Spoke to one of our team",
  "Other",
];

export const initialAccountValues = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  company: "",
  kind: "",
  role: "",
  headcountEstimate: "",
  website: "",
  surfacedFrom: "",
  password: "",
};

export const niceResponseTimeText = (avResponseTimeMinutes: number) => {
  if (avResponseTimeMinutes < 60) {
    return "an hour";
  } else if (avResponseTimeMinutes < 240) {
    return `${Math.ceil(avResponseTimeMinutes / 60)} hours`;
  } else if (avResponseTimeMinutes < 1440) {
    return "1 day";
  }
};

export const getBoundaries = (group: string) => {
  if (group === "New Faces") {
    return { lower: 300, middle: 600, upper: 601 };
  } else if (group === "Development") {
    return { lower: 400, middle: 800, upper: 801 };
  } else if (group === "Mainboard") {
    return { lower: 600, middle: 999, upper: 1000 };
  }
};

export const getPriceIndicator = (group: string, minDayRate?: number) => {
  let getPriceIndicator = null;
  const boundaries = getBoundaries(group);

  if (!minDayRate || !boundaries) {
    return null;
  } else if (minDayRate <= boundaries.lower) {
    getPriceIndicator = "£";
  } else if (minDayRate <= boundaries.middle) {
    getPriceIndicator = "££";
  } else if (minDayRate >= boundaries.upper) {
    getPriceIndicator = "£££";
  }
  return getPriceIndicator;
};
